import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./register-form.scss";
import { getRedirectResult, signInWithPopup, signInWithRedirect } from "firebase/auth";
import { auth, googleProvider } from "../firebase";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUser } from "../../../Slices/User/userDataSlice";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { authErrorToMessage } from "../errorMessages";
import request from "../../../utils/useApi";
import { get } from "../../../utils/request";
import { setCurrency } from "../../../utils/context";
import {
  exchangeGoogleSession,
  shouldFallbackToGoogleRedirect,
} from "../googleAuth";

const TERMS_ID = "68d5632c7063b3353409dd3d";
const PRIVACY_ID = "68d8d6e6ab9211f07264aac4";

const Modal = ({ open, title, html, loading, error, onClose }) => {
  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        <div className="modal-content">
          {loading && <p>Loading...</p>}
          {error && <p className="error-text">{error}</p>}
          {!loading && !error && html && (
            <div dangerouslySetInnerHTML={{ __html: html }} />
          )}
        </div>
        <button type="button" className="custom-btn small" onClick={onClose}>
          Close
        </button>
      </div>
    </div>,
    document.body
  );
};

const RegisterForm = ({ referralCode, goToSignIn = () => {}, goToOtpVerify = () => {}, onSocialLoginSuccess = () => {} }) => {
  const dispatch = useDispatch();

  const finishGoogleRegistration = useCallback(async (firebaseUser) => {
    const session = await exchangeGoogleSession(firebaseUser);
    dispatch(setUser(session));
    localStorage.setItem("teleport_token", session.token);
    sessionStorage.removeItem("teleport_google_registration_redirect");
    setCurrency(session.user.currency || "USD");
    onSocialLoginSuccess();
  }, [dispatch, onSocialLoginSuccess]);

  const [modal, setModal] = useState({
    open: false,
    title: "",
    html: "",
    loading: false,
    error: "",
  });

  // === Fetch article by _id (Terms/Privacy)
  const fetchArticle = async (id) => {
    setModal((m) => ({ ...m, loading: true, html: "", error: "" }));
    try {
      const res = await get(`/article/id/${id}`);

      const html = res?.content || "⚠️ Article content unavailable.";
      setModal((m) => ({ ...m, loading: false, html }));
    } catch (err) {
      console.error("Fetch failed:", err);
      setModal((m) => ({
        ...m,
        loading: false,
        error: "Failed to load content from Teleport Now API.",
      }));
    }
  };

  const openModal = (type) => {
    const id = type === "terms" ? TERMS_ID : PRIVACY_ID;
    const title = type === "terms" ? "Terms & Conditions" : "Privacy Policy";
    setModal({ open: true, title, html: "", loading: true, error: "" });
    fetchArticle(id);
  };

  const closeModal = () =>
    setModal({ open: false, title: "", html: "", loading: false, error: "" });

  useEffect(() => {
    let active = true;
    getRedirectResult(auth)
      .then(async (result) => {
        const registrationRedirect = sessionStorage.getItem(
          "teleport_google_registration_redirect"
        );
        if (!active || !registrationRedirect || !result?.user) return;
        await finishGoogleRegistration(result.user);
      })
      .catch((error) => {
        if (!active) return;
        console.error("Google redirect registration failed:", error);
        toast.error(
          error?.response?.data?.message ||
          authErrorToMessage(error?.code) ||
          error?.message ||
          "Google login failed."
        );
      });

    return () => {
      active = false;
    };
  }, [finishGoogleRegistration]);

  // === Validation schema
  const validationSchema = Yup.object({
    full_name: Yup.string().required("Full name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string()
      .required("Password is required")
      .min(8, "At least 8 characters")
      .matches(/[a-z]/, "One lowercase letter")
      .matches(/[A-Z]/, "One uppercase letter"),
    agree: Yup.bool().oneOf([true], "You must agree before continuing"),
    referralCode: Yup.string(),
  });

  // === Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      full_name: "",
      email: "",
      password: "",
      agree: false,
      referralCode: !!referralCode ? referralCode : '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        // const data = await createUserWithEmailAndPassword(
        //   auth,
        //   values.email,
        //   values.password
        // );

        const res = await request.post("/auth/signup", {
          fullName: values.full_name,
          email: values.email,
          password: values.password,
          referredBy: !!values.referralCode ? values.referralCode : "null",
        });

        if (res.status === 200) {
          const userId = res.data.user._id;
          goToOtpVerify(userId, values.email, values.password);
        } else {
          toast.error(res.data.message);
        }
      } catch (err) {
        console.log(err);
        const msg =
          err?.response?.data?.message ||
          authErrorToMessage(err?.code) ||
          "Unable to sign up. Please try again.";
        toast.error(msg);
      }
    },
  });

  // === Google SSO
  const handleGoogle = async () => {
    try {
      if (!formik.values.agree) {
        toast.error("To continue, please read and accept our Terms & Conditions and Privacy Policy");
        return;
      }

      const result = await signInWithPopup(auth, googleProvider);
      await finishGoogleRegistration(result.user);
    } catch (error) {
      console.error("Google sign-in failed:", error);
      if (shouldFallbackToGoogleRedirect(error)) {
        sessionStorage.setItem("teleport_google_registration_redirect", "true");
        await signInWithRedirect(auth, googleProvider);
        return;
      }
      toast.error(
        error?.response?.data?.message ||
        authErrorToMessage(error?.code) ||
        error?.message ||
        "Google login failed."
      );
    }
  };

  // === UI
  return (
    <div className="register-section">
      <form onSubmit={formik.handleSubmit} className="register-form">
        <div className="heading">
          <h2>Sign Up</h2>
          <p>Just a few taps to take off!</p>
        </div>

        <div className="content">
          {/* === Input fields === */}
          <div className="input-wrap">
            <label>Full name</label>
            <input
              type="text"
              name="full_name"
              placeholder="Enter full name"
              {...formik.getFieldProps("full_name")}
            />
            {formik.touched.full_name && formik.errors.full_name && (
              <div className="error">{formik.errors.full_name}</div>
            )}
          </div>

          <div className="input-wrap">
            <label>Email</label>
            <input
              type="email"
              name="email"
              placeholder="Enter email"
              {...formik.getFieldProps("email")}
            />
            {formik.touched.email && formik.errors.email && (
              <div className="error">{formik.errors.email}</div>
            )}
          </div>

          <div className="input-wrap">
            <label>Password</label>
            <input
              type="password"
              name="password"
              placeholder="Enter password"
              {...formik.getFieldProps("password")}
            />
            {formik.touched.password && formik.errors.password && (
              <div className="error">{formik.errors.password}</div>
            )}
          </div>

          <div className="input-wrap">
            <label>Referral Code</label>
            <input
              type="text"
              name="referralCode"
              placeholder="Referral Code"
              {...formik.getFieldProps("referralCode")}
              disabled={!!referralCode}
            />
          </div>

          {/* === Terms checkbox === */}
          <div className="terms-wrap">
            <label className="checkbox-container">
              <input
                type="checkbox"
                name="agree"
                {...formik.getFieldProps("agree")}
              />
              <span>
                I agree to the{" "}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => openModal("terms")}
                >
                  Terms & Conditions
                </button>{" "}
                and{" "}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => openModal("privacy")}
                >
                  Privacy Policy
                </button>
              </span>
            </label>
            {formik.touched.agree && formik.errors.agree && (
              <div className="error">{formik.errors.agree}</div>
            )}
          </div>

          <button
            className="custom-btn"
            type="submit"
            disabled={!formik.values.agree || formik.isSubmitting}
          >
            Create Account
          </button>

          {/* === Divider === */}
          <div className="social-auth">
            <div className="divider">
              <span>or sign up with</span>
            </div>

            <div className="social-icons">
              <button
                type="button"
                className="social-btn"
                onClick={handleGoogle}
                aria-label="Continue with Google"
              >
                <img src="/images/icons/google.svg" alt="" /> Google
              </button>
            </div>

            <p className="already-have">
              Already have an account? <Link to="/login" onClick={goToSignIn}>Sign In</Link>
            </p>
          </div>
        </div>
      </form>
      {/* === Terms / Privacy Modal === */}
      <Modal
        open={modal.open}
        title={modal.title}
        html={modal.html}
        loading={modal.loading}
        error={modal.error}
        onClose={closeModal}
      />
    </div>
  );
};

export default RegisterForm;
